'use client';
import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { QRCodeSVG } from 'qrcode.react';
import { Copy, CheckCheck, Share2, Link2, MessageCircle, Send, Mail, ArrowLeft, Sparkles, Users, IndianRupee, MousePointerClick, ExternalLink,  } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import DirectoryTopbar from '@/app/loan-aggregator-directory/components/DirectoryTopbar';
import { createClient } from '@/lib/supabase/client';
import { toast } from 'sonner';

interface ReferralStats {
  totalClicks: number;
  totalConversions: number;
  totalEarnings: number;
}

export default function ReferralLinkPage() {
  const router = useRouter();
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  const [stats, setStats] = useState<ReferralStats>({ totalClicks: 0, totalConversions: 0, totalEarnings: 0 });
  const [loadingStats, setLoadingStats] = useState(true);

  const baseUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://instantloanmitra.com';
  const refCode = user?.id?.slice(0, 8) || 'xxxxxxxx';
  const referralLink = `${baseUrl}?ref=${refCode}`;

  useEffect(() => {
    if (!user) return;
    const fetchStats = async () => {
      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('referral_tracking')
          .select('clicks, conversions, earnings_amount')
          .eq('user_id', user.id);

        if (!error && data) {
          const totalClicks = data.reduce((s, r) => s + (r.clicks || 0), 0);
          const totalConversions = data.reduce((s, r) => s + (r.conversions || 0), 0);
          const totalEarnings = data.reduce((s, r) => s + (r.earnings_amount || 0), 0);
          setStats({ totalClicks, totalConversions, totalEarnings });
        }
      } catch {
        // silently ignore
      } finally {
        setLoadingStats(false);
      }
    };
    fetchStats();
  }, [user]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error('Failed to copy. Please copy manually.');
    }
  };

  const shareMessage = `Apply for instant loans through India's best aggregators! Use my referral link and get started today: ${referralLink}`;

  const shareOptions = [
    {
      label: 'WhatsApp',
      icon: MessageCircle,
      color: '#25D366',
      bg: 'bg-[#25D366]/10 hover:bg-[#25D366]/20',
      border: 'border-[#25D366]/30',
      text: 'text-[#25D366]',
      href: `https://wa.me/?text=${encodeURIComponent(shareMessage)}`,
    },
    {
      label: 'Telegram',
      icon: Send,
      color: '#229ED9',
      bg: 'bg-[#229ED9]/10 hover:bg-[#229ED9]/20',
      border: 'border-[#229ED9]/30',
      text: 'text-[#229ED9]',
      href: `https://t.me/share/url?url=${encodeURIComponent(referralLink)}&text=${encodeURIComponent('Apply for instant loans through India\'s best aggregators!')}`,
    },
    {
      label: 'Twitter / X',
      icon: ExternalLink,
      color: '#1DA1F2',
      bg: 'bg-[#1DA1F2]/10 hover:bg-[#1DA1F2]/20',
      border: 'border-[#1DA1F2]/30',
      text: 'text-[#1DA1F2]',
      href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareMessage)}`,
    },
    {
      label: 'Facebook',
      icon: Share2,
      color: '#1877F2',
      bg: 'bg-[#1877F2]/10 hover:bg-[#1877F2]/20',
      border: 'border-[#1877F2]/30',
      text: 'text-[#1877F2]',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`,
    },
    {
      label: 'LinkedIn',
      icon: Users,
      color: '#0A66C2',
      bg: 'bg-[#0A66C2]/10 hover:bg-[#0A66C2]/20',
      border: 'border-[#0A66C2]/30',
      text: 'text-[#0A66C2]',
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralLink)}`,
    },
    {
      label: 'Email',
      icon: Mail,
      color: '#EA4335',
      bg: 'bg-[#EA4335]/10 hover:bg-[#EA4335]/20',
      border: 'border-[#EA4335]/30',
      text: 'text-[#EA4335]',
      href: `mailto:?subject=${encodeURIComponent('Apply for Instant Loans – My Referral Link')}&body=${encodeURIComponent(shareMessage)}`,
    },
  ];

  const handleNativeShare = async () => {
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({
          title: 'InstantLoanMitra – My Referral Link',
          text: 'Apply for instant loans through India\'s best aggregators!',
          url: referralLink,
        });
      } catch {
        // user cancelled
      }
    } else {
      handleCopy();
    }
  };

  const formatINR = (n: number) => {
    if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
    if (n >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
    return `₹${n.toFixed(0)}`;
  };

  return (
    <div className="min-h-screen bg-background">
      <DirectoryTopbar />

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Back */}
        <button
          onClick={() => router.back()}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <ArrowLeft size={15} />
          Back
        </button>

        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2.5 mb-1">
            <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
              <Link2 size={17} className="text-primary" />
            </div>
            <h1 className="text-xl font-extrabold text-foreground">Your Referral Link</h1>
          </div>
          <p className="text-sm text-muted-foreground ml-11.5">
            Share your unique link and earn when friends apply for loans.
          </p>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          {[
            { label: 'Total Clicks', value: loadingStats ? '—' : stats.totalClicks.toLocaleString('en-IN'), icon: MousePointerClick, color: 'text-blue-600', bg: 'bg-blue-50' },
            { label: 'Conversions', value: loadingStats ? '—' : stats.totalConversions.toLocaleString('en-IN'), icon: Users, color: 'text-violet-600', bg: 'bg-violet-50' },
            { label: 'Earnings', value: loadingStats ? '—' : formatINR(stats.totalEarnings), icon: IndianRupee, color: 'text-green-600', bg: 'bg-green-50' },
          ].map((s) => (
            <div key={s.label} className="bg-card rounded-2xl border border-border p-3.5 flex flex-col gap-1.5">
              <div className={`w-7 h-7 rounded-lg ${s.bg} flex items-center justify-center`}>
                <s.icon size={14} className={s.color} />
              </div>
              <p className="text-lg font-extrabold text-foreground leading-none">{s.value}</p>
              <p className="text-xs text-muted-foreground">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Link card */}
        <div className="bg-card rounded-2xl border border-border p-5 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={14} className="text-primary" />
            <span className="text-xs font-semibold text-primary uppercase tracking-wide">Your unique link</span>
          </div>

          {/* Link display + copy */}
          <div className="flex items-center gap-2 bg-muted rounded-xl px-3.5 py-3 mb-4">
            <span className="flex-1 text-sm font-medium text-foreground truncate select-all">{referralLink}</span>
            <button
              onClick={handleCopy}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex-shrink-0 ${
                copied
                  ? 'bg-green-100 text-green-700 border border-green-200' :'bg-primary text-white hover:bg-primary/90'
              }`}
            >
              {copied ? <CheckCheck size={13} /> : <Copy size={13} />}
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>

          {/* QR Code */}
          <div className="flex flex-col items-center gap-3 py-4 border-t border-border">
            <p className="text-xs text-muted-foreground font-medium">Scan to open your referral link</p>
            <div className="p-3 bg-white rounded-2xl border border-border shadow-sm">
              <QRCodeSVG
                value={referralLink}
                size={160}
                bgColor="#ffffff"
                fgColor="#111827"
                level="M"
                includeMargin={false}
              />
            </div>
            <p className="text-xs text-muted-foreground">Ref code: <span className="font-mono font-semibold text-foreground">{refCode}</span></p>
          </div>
        </div>

        {/* Share buttons */}
        <div className="bg-card rounded-2xl border border-border p-5 mb-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Share2 size={15} className="text-muted-foreground" />
              <span className="text-sm font-semibold text-foreground">Share via</span>
            </div>
            <button
              onClick={handleNativeShare}
              className="flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
            >
              <Share2 size={12} />
              More options
            </button>
          </div>

          <div className="grid grid-cols-3 gap-2.5">
            {shareOptions.map((opt) => (
              <a
                key={opt.label}
                href={opt.href}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex flex-col items-center gap-2 px-3 py-3.5 rounded-xl border ${opt.bg} ${opt.border} transition-all`}
              >
                <opt.icon size={20} className={opt.text} />
                <span className="text-xs font-medium text-foreground">{opt.label}</span>
              </a>
            ))}
          </div>
        </div>

        {/* How it works */}
        <div className="bg-primary/5 border border-primary/15 rounded-2xl p-5">
          <p className="text-sm font-semibold text-foreground mb-3">How referrals work</p>
          <div className="space-y-2.5">
            {[
              'Share your unique link with friends or on social media.',
              'When someone clicks your link and applies for a loan, it\u2019s tracked automatically.',
              'You earn a commission for every successful conversion.',
            ].map((step, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <span className="w-5 h-5 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                  {i + 1}
                </span>
                <p className="text-sm text-muted-foreground">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
